import React, {useMemo, useState} from 'react';
import {useQuery} from 'react-query';
import {
  DashboardLayout,
  EmptyState,
  Filter,
  FilterButton,
  Loader,
  PageLimit,
  Pagination,
  SkeletonLoader,
  TableLayout,
} from '../../../components';
import {
  baasBalance,
  baasMobile,
  backNofill,
  bank,
  copy,
  corruptedFile,
  green,
  red,
} from '../../../assets/images/images';
import {Link, useNavigate, useParams} from 'react-router-dom';
import BaasTransactionModal from './BaasTransactionModal';
import {
  fetchBAASCompanyWallet,
  fetchBAASTransactionAnalysis,
  fetchBAASTransactions,
} from '../../../requests/queries/baas';
import {filterOutEmptyValues, generateQueryParams} from '../../../helpers';
import {shortenLargeNumbers} from '../../../helpers/shortenLargeNumbers';
import {useAllApps} from '../../../hooks/useApps';
import moment from 'moment/moment';
import {getStatusTextAndStyles} from '../../../helpers/getStatusTextAndStyles';
import {typeAndStatusFilterOptions} from './constants';
import CardSummary from 'components/CardSummary';
import {handleCopy} from '.';
import AppliedFilters from 'components/AppliedFilters';

const initialFilters = {
  date_from: '',
  date_to: '',
  transaction_type: '',
};

function BaasAccountDetails() {
  const {slug} = useParams();
  const [openFilter, setOpenFilter] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [transactionDetails, setTransactionDetails] = useState(false);
  const {userApps, isLoading: appsLoading} = useAllApps();
  const navigate = useNavigate();
  const [filterProps, setFilterProps] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [searchText, setSearchText] = useState('');

  function shortenText(text, maxLength = 0) {
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 5)}......${text.slice(-3)}`;
  }

  const [query, setQuery] = useState({
    page: 1,
    length: 10,
  });

  const appId = useMemo(
    () => (!appsLoading && userApps.length > 0 ? userApps[0]?.value : ''),
    [appsLoading, userApps],
  );
  const toggleTransactionsModal = () =>
    setTransactionDetails(prevState => !prevState);

  const params = useMemo(
    () => ({
      wallet_id: slug,
      ...(appId && {app_id: appId}),
    }),
    [slug, appId],
  );

  const {data: wallets_details, isLoading} = useQuery(
    ['baas-wallet-details', {params}],
    () => fetchBAASCompanyWallet({params}),
  );

  const analysisParams = useMemo(
    () => ({
      start_date: filters?.start,
      end_date: filters?.end,
      transaction_type: filters?.transaction_type?.toString()?.toLowerCase(),
    }),
    [filters?.end, filters?.start, filters?.transaction_type],
  );

  const {data: transaction_analysis, isLoading: analysisLoading} = useQuery(
    ['baas-transaction-analysis', {params: {...params, ...analysisParams}}],
    () =>
      fetchBAASTransactionAnalysis({params: {...params, ...analysisParams}}),
  );

  const transactionParams = useMemo(
    () => ({
      ...params,
      ...query,
      start_date: filters?.start,
      end_date: filters?.end,
      transaction_type: filters?.transaction_type?.toString()?.toLowerCase(),
    }),
    [params, query, filters?.start, filters?.end, filters?.transaction_type],
  );

  const {data: wallet_transactions, isLoading: transactionsLoading} = useQuery(
    ['wallet-transactions', {params: transactionParams}],
    () => fetchBAASTransactions({params: transactionParams}),
    {enabled: appId?.length > 0},
  );

  const wallet_details = useMemo(
    () => !isLoading && wallets_details && wallets_details?.entity,
    [wallets_details, isLoading],
  );

  const walletTransactions = useMemo(
    () =>
      !transactionsLoading &&
      wallet_transactions &&
      wallet_transactions?.entity,
    [wallet_transactions, transactionsLoading],
  );

  const transactionAnalysis = useMemo(
    () =>
      !analysisLoading &&
      transaction_analysis &&
      transaction_analysis?.entity?.[0],
    [analysisLoading, transaction_analysis],
  );

  const pageClick = selected => {
    setQuery({
      ...query,
      page: selected,
    });
  };

  const handleFilter = (selectedOptions, shouldDownload = false) => {
    const filteredOptions = filterOutEmptyValues(selectedOptions);
    const queryParams = generateQueryParams(filteredOptions);
    const {status} = filteredOptions;
    if (Array.isArray(status)) {
      filteredOptions.status = status.join(',');
    }
    let baseUrl = '';
    if (queryParams) {
      baseUrl += `?${queryParams}`;
    }

    if (shouldDownload) {
      filteredOptions.download = true;
      setFilters({
        ...filters,
        ...filteredOptions,
      });
    } else {
      navigate(baseUrl);
    }

    setFiltersApplied(true);
    setFilters(filteredOptions);
    setOpenFilter(false);
    setFiltersApplied(
      Object.values(filteredOptions).some(value => value) ||
        searchText.length > 0,
    );
  };

  const handleResetFilter = () => {
    navigate('');
    setFilters(initialFilters);
    setOpenFilter(true);
    setSearchText('');
    setFiltersApplied(false);
  };

  const handleFilterStateChange = props => {
    setFilterProps(props);
    setFiltersApplied(
      Object.values(filters).some(value => value) || searchText.length > 0,
    );
  };

  const walletDetailsFields = {
    bankName: wallet_details?.account_numbers?.[0]?.bank_name,
    accountName: `${wallet_details?.account_numbers?.[0]?.first_name || ''} ${
      wallet_details?.account_numbers?.[0]?.last_name || ''
    }`,
    accountNumber: wallet_details?.account_numbers?.[0]?.account_number,
  };

  const isNotFiltered =
    !transactionsLoading &&
    (!walletTransactions?.transactions ||
      walletTransactions?.transactions.length === 0);
  const isFiltered = isNotFiltered && Object.values(filters).some(s => s);

  return (
    <DashboardLayout
      bg="bg-white80"
      topPadding="pt-8"
      xlLeftMargin="xl:ml-12"
      breadCrumbs={
        <div className="flex items-center">
          <img src={baasMobile} alt="" />
          <p className="ml-2 -mb-1">Wallets {'>'} Wallet Detail</p>
        </div>
      }
    >
      <div className="flex items-center justify-between mb-4 mt-10 lg:mt-0">
        <Link
          to={''}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2"
        >
          <img src={backNofill} alt="" />
          <span className="text-sm text-grey80 font-medium -mb-1">Back</span>
        </Link>
      </div>
      <div className="flex items-start lg:items-center lg:flex-row flex-col gap-5 justify-between p-5 lg:p-0">
        <div className="flex items-center gap-4 flex-wrap">
          <p className="text-grey font-medium text-[18px]">Wallet details</p>
          {filtersApplied && filterProps && (
            <AppliedFilters {...filterProps} isMainPage={true} />
          )}
        </div>

        <div className="flex items-center gap-2">
          <FilterButton
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            className="h-[48px] justify-center w-[130px]"
          />
        </div>
      </div>
      <Filter
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        handleFilter={handleFilter}
        resetUrl={handleResetFilter}
        filterOptions={[typeAndStatusFilterOptions[1]]}
        onFilterStateChange={handleFilterStateChange}
      />
      <div className="grid lg:grid-cols-2 gap-4 mt-7">
        <div className="flex flex-col gap-3">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2">
              <img alt="wallet" src={baasBalance} />

              <div className="flex flex-col gap-1">
                <p className="text-body font-medium text-sm">
                  Available Balance
                </p>
                {analysisLoading ? (
                  <SkeletonLoader
                    style={{
                      width: 150,
                      height: 25,
                      marginBottom: 10,
                    }}
                  />
                ) : (
                  <>
                    <h2 className="font-semibold text-[24px] text-grey">
                      {shortenLargeNumbers(
                        transactionAnalysis?.real_balance || 0,
                        'NGN',
                        {startAt: 'B'},
                      )}
                    </h2>
                    {/* <h4 className="mt-1 text-[10px] font-medium text-deepBlue300">
                      Ledger Balance:{' '}
                      <span className="ml-2 font-semibold text-[14px]">
                        {shortenLargeNumbers(
                          transactionAnalysis?.ledgerBalance || 0,
                          'NGN',
                          { startAt: 'B' },
                        )}
                      </span>
                    </h4> */}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-4 h-full">
            <CardSummary
              img={green}
              title="Total Collections"
              body={shortenLargeNumbers(
                transactionAnalysis?.sum_of_deposits || 0,
                'NGN',
                {startAt: 'B'},
              )}
              titleClassName="text-sm"
              bodyClassName="!text-[24px]"
              loading={isLoading}
            />
            <CardSummary
              img={red}
              title="Total Transfers"
              body={shortenLargeNumbers(
                transactionAnalysis?.sum_of_transfers || 0,
                'NGN',
                {startAt: 'B'},
              )}
              titleClassName="text-sm"
              bodyClassName="!text-[24px]"
              loading={isLoading}
            />
          </div>
        </div>
        <div className="bg-white rounded-lg p-8">
          <div className="flex items-center justify-between pb-3 border border-transparent border-b-[#E1E4EA]">
            <div className="flex items-center gap-2">
              <img src={bank} alt="bank" />
              <p className="font-medium text-black text-base">Wallet Details</p>
            </div>
            <button
              className="px-4 py-[6px] rounded-full border border-[#F2F4F7] text-sm text-black flex items-center gap-1  "
              onClick={() => handleCopy(walletDetailsFields)}
            >
              <img src={copy} alt="copy" />
              Copy
            </button>
          </div>
          <div className="mt-4 flex flex-col gap-8">
            {Object.entries(walletDetailsFields).map(([key, value]) => (
              <div
                key={key}
                className="flex items-center justify-between text-sm"
              >
                <p className="text-[#868C98] capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </p>
                {isLoading ? (
                  <SkeletonLoader
                    style={{
                      width: 150,
                      height: 25,
                      marginBottom: 10,
                    }}
                  />
                ) : (
                  <p className="text-black font-medium flex items-center   gap-2">
                    {value || 'N/A'}
                    {key === 'accountNumber' && (
                      <span
                        onClick={() =>
                          handleCopy(
                            wallet_details?.account_numbers?.[0]
                              ?.account_number,
                          )
                        }
                        className="cursor-pointer"
                      >
                        <img src={copy} alt="copy" />
                      </span>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <BaasTransactionModal
        show={transactionDetails}
        onClose={toggleTransactionsModal}
      />

      <TableLayout negativeMargins className="mt-4 w-full">
        {transactionsLoading ? (
          <Loader height={35} />
        ) : (
          <>
            <thead className="text-small font-semibold uppercase">
              <tr className="bg-white80">
                <th className="p-5 pl-12 font-medium text-left text-xs text-grey whitespace-nowrap">
                  date created
                </th>
                <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                  ref id
                </th>
                <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                  bank name
                </th>
                <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                  amount
                </th>
                <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                  type
                </th>
                <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                  balance
                </th>
                <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                  status
                </th>
              </tr>
            </thead>
            <tbody className="text-sm text-body">
              {transactionsLoading ? (
                <tr>
                  <td colSpan={7}>
                    <Loader height={35} />
                  </td>
                </tr>
              ) : walletTransactions?.transactions?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-4 text-center">
                    {isFiltered ? (
                      <EmptyState
                        height={45}
                        src={corruptedFile}
                        noBtn
                        customBtn={
                          <button
                            onClick={() => {
                              navigate('');
                              setFilters(initialFilters);
                              setSearchText('');
                              setFiltersApplied(false);
                              setOpenFilter(true);
                            }}
                            className="text-brandBlue p-4 text-sm font-medium"
                          >
                            Update preferences
                          </button>
                        }
                        heading="No Transactions Found"
                        body="Oops, it looks like there are no transactions that match the filters you've selected. Please try adjusting your filters."
                      />
                    ) : (
                      <EmptyState
                        height={45}
                        src={corruptedFile}
                        noBtn
                        className="w-full"
                        heading="No Transactions Found"
                        body="No transactions found for this wallet"
                        maxWidth="max-w-[500px]"
                      />
                    )}
                  </td>
                </tr>
              ) : (
                walletTransactions?.transactions.map((transaction, index) => (
                  <tr
                    key={index}
                    className="text-left border-b bg-white border-grey60"
                  >
                    <td className="p-4 pl-12 whitespace-nowrap">
                      {(transaction?.date_created &&
                        moment(transaction?.date_created).format(
                          'DD MMM YYYY h:m',
                        )) ||
                        '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {shortenText(transaction?.transaction_id || '--', 21) ||
                        '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {transaction?.recipient_bank_name || '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {shortenLargeNumbers(
                        transaction?.transaction_amount || 0,
                        'NGN',
                        {startAt: 'B'},
                      )}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <span
                        className={`${
                          getStatusTextAndStyles(
                            transaction?.transaction_type?.toLowerCase(),
                          )?.solid
                        } uppercase   w-fit rounded-full px-2 py-1 h-5 flex items-center justify-center text-xs`}
                      >
                        {transaction?.transaction_type === 'transfer'
                          ? 'Debit'
                          : 'Credit' || '-'}
                      </span>
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {shortenLargeNumbers(
                        transaction?.wallet_balance_after || 0,
                        'NGN',
                        {startAt: 'B'},
                      )}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <span
                        className={`${
                          getStatusTextAndStyles(
                            transaction?.transaction_status?.toLowerCase(),
                          )?.styles
                        } uppercase w-fit rounded-full px-2 py-1 h-5   flex items-center justify-center text-xs`}
                      >
                        {
                          getStatusTextAndStyles(
                            transaction?.transaction_status?.toLowerCase(),
                          )?.text
                        }
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </>
        )}
      </TableLayout>

      {!isLoading && walletTransactions?.transactions?.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 mt-12 sm:mt-12 pl-12 pr-6">
          <PageLimit
            onLimitChange={length => {
              setQuery(prevState => ({
                ...prevState,
                length,
                page: 1,
              }));
            }}
            limit={query?.length}
            total={walletTransactions?.total_pages}
            length={walletTransactions?.transactions?.length}
          />

          <Pagination
            total={walletTransactions?.total_pages}
            current={Number(walletTransactions?.current_page)}
            onPageChange={activePage => {
              pageClick(activePage);
              const searchParams = new URLSearchParams(
                document.location.search,
              );
              searchParams.set('page', activePage);
              const newSearchParams = searchParams.toString();
              navigate(`?${newSearchParams}`);
            }}
          />
        </div>
      )}
    </DashboardLayout>
  );
}

export default BaasAccountDetails;
